import api from './api'

export interface UniversityBranding {
  name: string
  slug: string
  institution_country?: string | null
  branding: {
    programs_per_page?: number
    colors?: {
      primary?: string
      secondary?: string
      accent?: string
      footer_background?: string
      program_finder_background?: string
    }
    fonts?: {
      university_name?: string
      heading?: string
      body?: string
    }
    logo?: string
    favicon?: string
    hero_image?: string
    landing_page?: {
      title?: string
      subtitle?: string
      cta_text?: string
    }
    footer_links?: Array<{
      label: string
      url: string
    }>
    social_media?: {
      facebook?: string
      twitter?: string
      instagram?: string
      linkedin?: string
      youtube?: string
    }
    contact_email?: string
    phone_number?: string
    address?: string
    footer_text?: string
    hidden_fields?: string[]
    stages?: Array<{ label: string; value: string }>
    application_statuses?: Array<{ label: string; value: string }>
    // CV/Education picklist values from portal config
    degrees?: Array<{ label: string; value: string }>
    hide_gpa?: boolean
    grading_systems?: Array<{ label: string; value: string }>
    proficiency_exams?: Array<{ label: string; value: string }>
    job_types?: Array<{ label: string; value: string }>
    gender_identities?: Array<{ label: string; value: string }>
    // Feature toggles
    show_transfer_option?: boolean
    show_exchange_option?: boolean
    // Bypass flags
    bypass_cv_form?: boolean
    bypass_email_verification?: boolean
    // Portal UI links
    privacy_policy_url?: string
    terms_of_service_url?: string
    accessibility_url?: string
    // Raw HTML injected into <head> on every page (like Salesforce Experience Cloud Head Markup)
    head_markup?: string
    // Bank account details shown to members when they choose bank transfer
    bank_transfer_info?: string
    personal_info_form_config?: Record<string, {
      is_visible: boolean
      is_required: boolean
      salesforce_api_name: string
      field_type: 'text' | 'picklist' | 'date' | 'email' | 'phone' | 'checkbox' | 'country'
      label: string
    }>
  }
}

export interface University {
  name: string
  slug: string
  domain: string
}

export interface HubProgram {
  id: number
  price_list_entry_id: string
  name: string
  program_code: string
  university_name: string
  faculty?: string
  campus_location?: string
  description?: string
  about_program?: string
  degree_type: string // "Bachelor", "Master", "PhD"
  discipline: string
  language: string
  mode_of_study: string
  thesis_requirement?: string
  duration?: string
  tuition_fee?: number | null
  discounted_tuition_fee?: number | null
  currency_code?: string
  requires_job_experience: boolean
  requires_proficiency_exam: boolean
  requires_references: boolean
  is_active: boolean
}

export interface ProgramsResponse {
  status: string
  count: number
  programs: HubProgram[]
}

export interface IntakeTerm {
  id: string
  name: string
  salesforce_id?: string
  available_application_types?: string[]
}

export interface TermsResponse {
  status: string
  count: number
  terms: IntakeTerm[]
}

export interface Degree {
  id: string
  degree: string
  available_application_types?: string[]
  max_ple_for_same_institution: number | null
  max_application: number  // always resolved (never null) from API
}

export interface DegreesResponse {
  status: string
  count: number
  degrees: Degree[]
}

/**
 * Fetch university branding by slug
 */
export const getUniversityBranding = async (slug: string): Promise<UniversityBranding> => {
  const response = await api.get(`/universities/${slug}/branding/`)
  return response.data
}

/**
 * Fetch university branding by domain
 */
export const getUniversityBrandingByDomain = async (domain: string): Promise<UniversityBranding> => {
  const response = await api.get(`/universities/branding/?domain=${domain}`)
  return response.data
}

/**
 * Fetch all universities
 */
export const getUniversities = async (): Promise<University[]> => {
  const response = await api.get('/universities/')
  return response.data
}

/**
 * Fetch university programs by slug
 */
export const getUniversityPrograms = async (slug: string): Promise<ProgramsResponse> => {
  const response = await api.get(`/universities/${slug}/programs/`)
  return response.data
}

/**
 * Fetch programs from Hub DB filtered by intake term, degree type, and application type.
 * Direct database query — no external HTTP callout.
 */
export const getProgramsByTerm = async (
  termId: string,
  degreeType: string,
  applicationType?: string
): Promise<ProgramsResponse> => {
  const response = await api.get('/integrations/hub/programs-by-term/', {
    params: {
      term_id: termId,
      degree_type: degreeType,
      application_type: applicationType || 'First Year Student',
    },
  })
  return response.data
}

/**
 * Fetch intake terms from Hub API via integrations endpoint
 */
export const getIntakeTerms = async (): Promise<TermsResponse> => {
  const response = await api.get('/integrations/hub/terms/')
  return response.data
}

export const getDegrees = async (termId?: string): Promise<DegreesResponse> => {
  const params = termId ? { term_id: termId } : undefined
  const response = await api.get('/integrations/hub/degrees/', { params })
  return response.data
}

export interface CreateApplicationRequest {
  selected_entries: string[]
  term_id: string
  degree_setting_id: string
  application_type?: string
  application_number?: string
}

export interface CreateApplicationResponse {
  status: string
  code?: string              // e.g. "max_application_reached"
  max_application?: number   // present when code === "max_application_reached"
  // Universities that do not accept online applications; present when
  // code === "institution_not_accepting_applications".
  institutions?: string[]
  detail?: string
  result?: {
    applicationNumber?: string
    applicationSalesforceId?: string
    [key: string]: unknown
  }
  debug?: boolean
}

/**
 * Create application(s) in Hub for the authenticated applicant.
 * Called when the applicant selects a program and navigates to the next step.
 */
export const createApplication = async (
  params: CreateApplicationRequest
): Promise<CreateApplicationResponse> => {
  try {
    const response = await api.post('/integrations/hub/applications/create/', params)
    return response.data
  } catch (error: unknown) {
    // Axios throws on 4xx/5xx — extract the response body so callers can
    // inspect structured error payloads (e.g. max_application_reached) instead
    // of only receiving a generic network error.
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response
    ) {
      return error.response.data as CreateApplicationResponse
    }
    throw error
  }
}

export interface CountriesResponse {
  status: string
  count: number
  countries: string[]
}

/**
 * Fetch countries list from Hub DB via integrations endpoint
 */
export const getCountries = async (): Promise<CountriesResponse> => {
  const response = await api.get('/integrations/hub/countries/')
  return response.data
}

export interface ApplicantProfile {
  first_name?: string
  last_name?: string
  email?: string
  preferred_name?: string
  citizenship?: string
  secondary_citizenship?: string
  gender?: string
  national_identity_number?: string
  date_of_birth?: string
  country_of_birth?: string
  city_of_birth?: string
  phone?: string
  country_phone_code?: string
  father_name?: string
  mother_name?: string
  address_country?: string
  address_street?: string
  address_city?: string
  address_zip?: string
  // Identity document (passport)
  identity_number?: string
  identity_date_of_issue?: string
  identity_date_of_expiry?: string
  identity_issuing_country?: string
}

export interface ApplicantProfileResponse {
  status: string
  profile: ApplicantProfile
}

/**
 * Fetch the authenticated applicant's profile from Hub DB university_person table
 */
export const getApplicantProfile = async (): Promise<ApplicantProfileResponse> => {
  const response = await api.get('/integrations/hub/applicant-profile/')
  return response.data
}

export interface SubmitApplicationRequest {
  application_number: string
}

export interface SubmitApplicationResponse {
  status: string
  application_number?: string
  message?: string
}

/**
 * Submit a completed application — transitions it from draft to submitted state.
 * Called when the applicant clicks "Submit Application" on the final step.
 */
export const submitApplication = async (
  params: SubmitApplicationRequest
): Promise<SubmitApplicationResponse> => {
  const response = await api.post('/integrations/hub/applications/submit/', params)
  return response.data
}

export interface ApplicationDetailResponse {
  status: string
  term_id: string | null
  degree_id: string | null
  degree_type: string | null
  price_list_entry_id: number | null
  interested_programs: number[]
  applied_program_name: string
  applied_term_name: string
  degree_level: string
  application_type: string
}


export const getApplicationDetail = async (appNumber: string): Promise<ApplicationDetailResponse> => {
  const response = await api.get(`/integrations/hub/applications/${appNumber}/detail/`)
  return response.data
}

export interface PersonalInfoPayload {
  first_name?: string
  last_name?: string
  preferred_name?: string
  citizenship?: string
  secondary_citizenship?: string
  gender?: string
  national_identity_number?: string
  date_of_birth?: string
  country_of_birth?: string
  city_of_birth?: string
  phone?: string
  country_phone_code?: string
  father_name?: string
  mother_name?: string
  address_country?: string
  address_street?: string
  address_city?: string
  address_zip?: string
  university_alumni?: boolean
  application_salesforce_id?: string
  // Identity document (passport)
  identity_number?: string
  identity_date_of_issue?: string
  identity_date_of_expiry?: string
  identity_issuing_country?: string
}

/**
 * Save personal information step to Hub DB and sync to Salesforce.
 * Called when the applicant advances past step 1 (Personal Information).
 */
export const savePersonalInfo = async (
  appNumber: string,
  payload: PersonalInfoPayload
): Promise<{ status: string; salesforce_id?: string; account_salesforce_id?: string }> => {
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/personal-info/`,
    payload
  )
  return response.data
}

// ── Educational History ────────────────────────────────────────────────────

export interface EducationRecord {
  id?: number | string
  name?: string
  degree?: string
  field?: string
  country?: string
  gpa?: string
  gpaType?: string
  year?: string
  beginMonth?: string
  beginYear?: string
  endMonth?: string
  endYear?: string
}

export interface ExamRecord {
  id?: number | string
  name?: string
  score?: string
  examMonth?: string
  examYear?: string
}

export interface ExperienceRecord {
  id?: number | string
  company?: string
  role?: string
  jobType?: string
  country?: string
  city?: string
  currentlyWorking?: boolean
  beginMonth?: string
  beginYear?: string
  endMonth?: string
  endYear?: string
}

export interface ReferenceRecord {
  id?: number | string
  firstName?: string
  lastName?: string
  name?: string
  email?: string
  phone?: string
  company?: string
  jobTitle?: string
}

export type EducationalHistorySection = 'education' | 'exam' | 'experience' | 'reference'

/**
 * Save one educational history section to Hub API.
 * Called after each save in the EducationalHistory step.
 * Hub API failure is non-blocking on the backend — user always advances.
 */
export interface SavedIdMapping {
  frontend_id: number | string
  db_id: number
  salesforce_id?: string | null
}

export const saveEducationalHistory = async (
  appNumber: string,
  section: EducationalHistorySection,
  records: EducationRecord[] | ExamRecord[] | ExperienceRecord[] | ReferenceRecord[]
): Promise<{ status: string; saved?: SavedIdMapping[]; detail?: string }> => {
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/educational-history/`,
    { section, records }
  )
  return response.data
}

export interface EducationalHistoryData {
  status: string
  education: EducationRecord[]
  exams: ExamRecord[]
  experiences: ExperienceRecord[]
  references: ReferenceRecord[]
}

export const getEducationalHistory = async (appNumber: string): Promise<EducationalHistoryData> => {
  const response = await api.get(
    `/integrations/hub/applications/${appNumber}/educational-history/`
  )
  return response.data
}

/**
 * Trigger a Salesforce → Hub inbound sync for all CV record types for this
 * member + application.  Only records not yet in the Hub DB are inserted.
 *
 * Rate-limited to once per minute per user (cooldown enforced server-side).
 * When cooldown is active the server returns `{ status: "cooldown" }` which
 * the caller should treat identically to success.
 *
 * This is always non-fatal: the caller should proceed regardless of outcome.
 */
export const syncCvFromSalesforce = async (
  appNumber: string
): Promise<{ status: string; inserted_counts?: Record<string, number> }> => {
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/educational-history/sync/`
  )
  return response.data
}

// ── Questionnaire ──────────────────────────────────────────────────────────

export interface CvQuestion {
  id: string                  // salesforce_curriculum_vitae_id
  question: string
  answer: string | null
  public_url: string | null
  document_id: number | null  
  question_data_type: string  // 'Text' | 'Picklist' | 'Boolean' | 'Date' | 'Number'
  picklist_values: string | null  // semicolon-separated options e.g. "Yes;No;Maybe"
  required: boolean
  section_name: string | null
  display_condition: string | null
  description: string | null
  sort_order: number | null
}

export interface QuestionnaireResponse {
  status: string
  count: number
  questions: CvQuestion[]
}

/**
 * Fetch all Questionnarie CV questions for a given application.
 * Returns empty list if Hub DB is not configured or no questions exist.
 */
export const getQuestionnaire = async (appNumber: string): Promise<QuestionnaireResponse> => {
  const response = await api.get(
    `/integrations/hub/applications/${appNumber}/questionnaire/`
  )
  return response.data
}

/**
 * Trigger a Salesforce → Hub inbound sync for Questionnarie CV records for
 * this member + application.  Existing rows are updated in place; new records
 * are inserted.
 *
 * Rate-limited to once per minute per user (cooldown enforced server-side).
 * When cooldown is active the server returns `{ status: "cooldown" }` which
 * the caller should treat identically to success.
 *
 * Always non-fatal: the caller should proceed to getQuestionnaire regardless.
 */
export const syncQuestionnaireFromSalesforce = async (
  appNumber: string
): Promise<{ status: string }> => {
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/questionnaire/sync/`
  )
  return response.data
}

/**
 * Save questionnaire answers via the existing hub_cv_upsert endpoint.
 * Each answer is { salesforce_id, answer } — the CV record's SF ID + the user's answer.
 */
export const saveQuestionnaireAnswers = async (
  appNumber: string,
  answers: { salesforce_id: string; answer: string }[]
): Promise<{ status: string }> => {
  const response = await api.post('/integrations/hub/cv/salesforce/', {
    application_id: appNumber,
    cv_records: answers.map((a) => ({
      record_type_developer_name: 'Questionnarie',
      salesforce_id: a.salesforce_id,
      answer: a.answer,
    })),
  })
  return response.data
}

// ── Documents ──────────────────────────────────────────────────────────────

export interface CvDocument {
  id: string              
  db_id: number | null    
  document_name: string
  status: string | null   // 'Pending' | 'Approved' | 'Rejected' | null
  required: boolean
  description: string | null
  section_name: string | null
  public_url: string | null
  message: string | null
  document_maximum_size: number | null  // bytes
  document_type: string | null
  setting_record_id: string
}

export interface DocumentsResponse {
  status: string
  count: number
  documents: CvDocument[]
}

/**
 * Fetch document requirements for the given application from Hub DB.
 */
export const getDocuments = async (appNumber: string): Promise<DocumentsResponse> => {
  const response = await api.get(`/integrations/hub/applications/${appNumber}/documents/`)
  return response.data
}

/**
 * Trigger a Salesforce → Hub inbound sync for Document_Requirement CV records
 * for this member + application.  SF document statuses (Approved, Rejected,
 * Pending, etc.) are written back to the local Document rows so the member
 * sees up-to-date review results without a manual refresh.
 *
 * Rate-limited to once per minute per user (cooldown enforced server-side).
 * When cooldown is active the server returns `{ status: "cooldown" }` which
 * the caller should treat identically to success.
 *
 * Always non-fatal: the caller should proceed to getDocuments regardless.
 */
export const syncDocumentsFromSalesforce = async (
  appNumber: string
): Promise<{ status: string }> => {
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/documents/sync/`
  )
  return response.data
}

/**
 * Upload a single document file to GCS via the backend.
 * Uses multipart/form-data. Returns GCS path on success.
 */
export const uploadDocument = async (
  appNumber: string,
  settingRecordId: string,
  file: File,
  onProgress?: (pct: number) => void,
  documentName?: string,
): Promise<{ status: string; document_id: number; gcs_path: string }> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('setting_record_id', settingRecordId)
  if (documentName) formData.append('document_name', documentName)
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/documents/upload/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)) }
        : undefined,
    }
  )
  return response.data
}

// ── CV Requirements ────────────────────────────────────────────────────────

export interface CvCriteria {
  sort_order: number
  field_api_name: string
  operator: string
  field_value: string
  is_active: boolean
}

export interface CvRequirement {
  id: number
  name: string
  message: string
  result: string         
  section_key: string
  criteria_logic: string
  has_program: boolean   
  is_required: boolean
  required_count: number | null 
  criteria: CvCriteria[]
}

export interface CvFieldMapping {
  local_field: string
  local_model: string
}

export interface CvRequirementsResponse {
  status: string
  requirements: CvRequirement[]
  field_mappings: Record<string, CvFieldMapping>
}

export const getCvRequirements = async (appNumber: string): Promise<CvRequirementsResponse> => {
  const response = await api.get(`/integrations/hub/applications/${appNumber}/cv-requirements/`)
  return response.data
}

export const deleteEducationalHistoryRecord = async (
  appNumber: string,
  section: EducationalHistorySection,
  recordId: number
): Promise<{ status: string }> => {
  const response = await api.delete(
    `/integrations/hub/applications/${appNumber}/educational-history/`,
    { data: { section, record_id: recordId } }
  )
  return response.data
}

export const deleteDocument = async (
  appNumber: string,
  documentId: number
): Promise<{ status: string }> => {
  const response = await api.delete(
    `/integrations/hub/applications/${appNumber}/documents/${documentId}/delete/`
  )
  return response.data
}


export const fetchDocumentBlobUrl = async (documentId: number): Promise<{ blobUrl: string; mimeType: string }> => {
  const response = await api.get(
    `/integrations/hub/documents/${documentId}/download/`,
    { responseType: 'blob' }
  )
  const blob: Blob = response.data
  return { blobUrl: URL.createObjectURL(blob), mimeType: blob.type }
}

export const uploadQuestionnaireFile = async (
  appNumber: string,
  salesforceId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ status: string; gcs_path: string; cv_sf_id: string | null }> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('salesforce_id', salesforceId)
  const response = await api.post(
    `/integrations/hub/applications/${appNumber}/questionnaire/upload/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)) }
        : undefined,
    }
  )
  return response.data
}

export const deleteQuestionnaireFile = async (
  appNumber: string,
  salesforceId: string,
): Promise<{ status: string }> => {
  const response = await api.delete(
    `/integrations/hub/applications/${appNumber}/questionnaire/${encodeURIComponent(salesforceId)}/delete/`
  )
  return response.data
}
